import { z } from "zod";
import { IdSchema, IsoDateTimeSchema } from "./primitives.js";
/**
 * The clinician's in-progress note for one encounter, kept on the server between the commits
 * that write a note version (order preparation, referral drafting, sign). One overwritten row per
 * clinician per encounter: saving it writes no note version and leaves the encounter version
 * alone, so prepared orders stay bound to what they were prepared from. It is deleted when the
 * encounter is signed or cancelled (api.save_note_working_draft and friends).
 */
export const NOTE_WORKING_DRAFT_SECTION_MAX_CHARS = 50_000;
export const NOTE_WORKING_DRAFT_INTENT_MAX_CHARS = 12_000;
export const NoteWorkingDraftSectionsSchema = z
    .object({
    subjective: z.string().max(NOTE_WORKING_DRAFT_SECTION_MAX_CHARS),
    objective: z.string().max(NOTE_WORKING_DRAFT_SECTION_MAX_CHARS),
    assessment: z.string().max(NOTE_WORKING_DRAFT_SECTION_MAX_CHARS),
    plan: z.string().max(NOTE_WORKING_DRAFT_SECTION_MAX_CHARS),
})
    .strict();
/**
 * The parts of the Assessment and Plan the clinician wrote (as opposed to generated text), so a
 * draft reopened on another device keeps that distinction.
 */
export const NoteWorkingDraftIntentSchema = z
    .object({
    assessment: z.string().max(NOTE_WORKING_DRAFT_INTENT_MAX_CHARS),
    plan: z.string().max(NOTE_WORKING_DRAFT_INTENT_MAX_CHARS),
})
    .strict();
export const SaveNoteWorkingDraftRequestSchema = z
    .object({
    sections: NoteWorkingDraftSectionsSchema,
    clinicianIntent: NoteWorkingDraftIntentSchema.optional(),
    /** The saved note version this draft was edited from; 0 when the encounter has none yet. */
    baseNoteVersion: z.number().int().nonnegative(),
    /**
     * The draft revision this save replaces, or null for the first save. A different revision on
     * the server (another tab or device saved since) is refused as a conflict, never overwritten.
     */
    expectedRevision: z.number().int().positive().nullable(),
})
    .strict();
export const NoteWorkingDraftReceiptSchema = z
    .object({
    encounterId: IdSchema,
    revision: z.number().int().positive(),
    baseNoteVersion: z.number().int().nonnegative(),
    savedAt: IsoDateTimeSchema,
})
    .strict();
export const NoteWorkingDraftSchema = NoteWorkingDraftReceiptSchema.extend({
    sections: NoteWorkingDraftSectionsSchema,
    clinicianIntent: NoteWorkingDraftIntentSchema.nullable(),
}).strict();
export const NoteWorkingDraftDeleteReceiptSchema = z
    .object({ encounterId: IdSchema, deleted: z.boolean() })
    .strict();
//# sourceMappingURL=note-working-draft.js.map